import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Gift, Copy, Check, Wallet, Users, TrendingUp, 
  Share2, Twitter, Send, MessageCircle, Clock,
  Sparkles, Crown, ArrowRight, Target, Zap,
  CheckCircle, Timer, Gem, Coins, Info, Eye, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const ReferralWalletPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  
  // Mesaj önizleme modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState(null);
  const [previewText, setPreviewText] = useState('');
  const [textCopied, setTextCopied] = useState(false);

  // Hedef: 5 Pro = 1 ay ücretsiz (~₺590)
  const TARGET_PRO_COUNT = 5;
  const MONTHLY_PRICE = 590;
  const AVG_COMMISSION = 100; // Ortalama komisyon (indirimli fiyat üzerinden %20)

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [codeRes, walletRes, statsRes] = await Promise.all([
        axios.get('/referral/my-code'),
        axios.get('/referral/wallet'),
        axios.get('/referral/stats')
      ]);
      
      setReferralData(codeRes.data);
      setWalletData(walletRes.data);
      setStatsData(statsRes.data);
    } catch (error) {
      console.error('Error loading referral data:', error);
      toast.error('Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (referralData?.code) {
      navigator.clipboard.writeText(referralData.code);
      setCopied(true);
      toast.success('Kod kopyalandı!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyLink = () => {
    if (referralData?.share_url) {
      navigator.clipboard.writeText(referralData.share_url);
      setCopiedLink(true);
      toast.success('Link kopyalandı!');
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  // Hazır paylaşım metinleri - Kişiselleştirilmiş
  const getShareText = (platform) => {
    const code = referralData?.code || '';
    const link = referralData?.share_url || 'https://borsamex.com';
    
    if (platform === 'whatsapp') {
      return `🚀 Ben Borsamex'te AI destekli kripto sinyallerini kullanıyorum

💰 Sana özel kodum: ${code}
🎁 Bu kodla %15 indirimle başlayabilirsin

📊 Canlı sinyaller + performans takibi var

👉 ${link}`;
    } else if (platform === 'telegram') {
      return `🚀 Borsamex'te AI kripto sinyalleri kullanıyorum

💰 Kodum: ${code}
🎁 %15 indirimle başla

👉 ${link}`;
    } else {
      // Twitter - kısa ve öz
      return `Borsamex'te AI kripto sinyalleri kullanıyorum 🚀

Kodum ${code} ile %15 indirimli başla 👇`;
    }
  };

  // Önizleme aç
  const openPreview = (platform) => {
    setPreviewPlatform(platform);
    setPreviewText(getShareText(platform));
    setTextCopied(false);
    setPreviewOpen(true);
  };

  // Paylaşımı gerçekleştir
  const executeShare = () => {
    const url = referralData?.share_url || 'https://borsamex.com';
    
    if (previewPlatform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(previewText)}`, '_blank');
    } else if (previewPlatform === 'telegram') {
      window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(previewText)}`, '_blank');
    } else if (previewPlatform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(previewText)}&url=${encodeURIComponent(url)}`, '_blank');
    }
    
    setPreviewOpen(false);
    toast.success('Paylaşım penceresi açıldı!');
  };

  // Mesajı kopyala - mikro feedback ile
  const copyShareText = () => {
    navigator.clipboard.writeText(previewText);
    setTextCopied(true);
    toast.success('Mesaj kopyalandı!');
    setTimeout(() => setTextCopied(false), 2000);
  };

  const shareOnTwitter = () => {
    openPreview('twitter');
  };

  const shareOnTelegram = () => {
    openPreview('telegram');
  };

  const shareOnWhatsApp = () => {
    openPreview('whatsapp');
  };

  // Status badge helper
  const getStatusBadge = (status) => {
    switch(status) {
      case 'converted':
        return (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
            <Gem className="h-3 w-3 mr-1" /> Pro Oldu
          </Badge>
        );
      case 'registered':
        return (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
            <Timer className="h-3 w-3 mr-1" /> Ödeme Bekleniyor
          </Badge>
        );
      default:
        return (
          <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
            <CheckCircle className="h-3 w-3 mr-1" /> Kayıt Oldu
          </Badge>
        );
    }
  };

  // Progress hesaplama
  const proCount = statsData?.converted_referrals || 0;
  const progressPercent = Math.min((proCount / TARGET_PRO_COUNT) * 100, 100);
  const remainingForFreeMonth = Math.max(TARGET_PRO_COUNT - proCount, 0);

  if (!user) {
    return (
      <div className="min-h-screen bg-brand-dark py-12">
        <div className="container mx-auto px-6 max-w-lg text-center">
          <Gift className="h-16 w-16 text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Kupon Kardeşliği</h1>
          <p className="text-slate-400 mb-6">
            Arkadaşlarınızı davet edin, birlikte kazanın!
          </p>
          <Button onClick={() => navigate('/login')}>
            Giriş Yap
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="flex items-center justify-center h-64">
            <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark py-12">
      <div className="container mx-auto px-6 max-w-4xl">
        
        {/* Mesaj Önizleme Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                Mesaj Önizlemesi
              </DialogTitle>
              <DialogDescription className="flex flex-col gap-1">
                <span>
                  {previewPlatform === 'whatsapp' && 'WhatsApp\'ta paylaşılacak mesaj'}
                  {previewPlatform === 'telegram' && 'Telegram\'da paylaşılacak mesaj'}
                  {previewPlatform === 'twitter' && 'Twitter\'da paylaşılacak mesaj'}
                </span>
                <span className="text-xs text-slate-500 italic">
                  Bu mesaj karşı tarafa aynen bu şekilde gidecek.
                </span>
              </DialogDescription>
            </DialogHeader>
            
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 max-h-60 overflow-y-auto">
              <pre className="text-sm text-slate-200 whitespace-pre-wrap font-sans">
                {previewText}
              </pre>
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button 
                variant="outline" 
                onClick={copyShareText}
                className="w-full sm:w-auto"
              >
                {textCopied ? (
                  <>
                    <Check className="h-4 w-4 mr-2 text-green-400" />
                    Kopyalandı ✓
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Metni Kopyala
                  </>
                )}
              </Button>
              <Button 
                onClick={executeShare}
                className={`w-full sm:w-auto ${
                  previewPlatform === 'whatsapp' ? 'bg-green-600 hover:bg-green-700' :
                  previewPlatform === 'telegram' ? 'bg-blue-500 hover:bg-blue-600' :
                  'bg-primary hover:bg-primary/90'
                }`}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {previewPlatform === 'whatsapp' && 'WhatsApp\'ta Aç'}
                {previewPlatform === 'telegram' && 'Telegram\'da Aç'}
                {previewPlatform === 'twitter' && 'Twitter\'da Aç'}
              </Button>
            </DialogFooter>
            
            {/* Mobil ipucu */}
            {previewPlatform === 'whatsapp' && (
              <p className="text-[11px] text-slate-500 text-center -mt-2">
                📱 Mobilde doğrudan WhatsApp açılır
              </p>
            )}
          </DialogContent>
        </Dialog>

        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary/50">
            <Gift className="h-3 w-3 mr-1" />
            Kupon Kardeşliği
          </Badge>
          <h1 className="text-3xl font-bold mb-3">Davet Et, Birlikte Kazan!</h1>
          
          {/* Ana değer önerisi - net ve kısa */}
          <p className="text-lg text-slate-300 mb-2">
            Arkadaşın <span className="text-amber-400 font-semibold">%15 indirimle</span> başlar, 
            sen <span className="text-primary font-semibold">%20 kredi</span> kazanırsın.
          </p>
          <p className="text-sm text-slate-500 flex items-center justify-center gap-1">
            <Info className="h-3 w-3" />
            Krediler üyelik yenilemende kullanılır.
          </p>
        </div>

        {/* Hedef Progress Bar - ÖNEMLİ */}
        <Card className="mb-6 border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-orange-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-400" />
                <span className="font-semibold text-white">1 Ay Ücretsiz Pro Hedefi</span>
              </div>
              <span className="text-sm text-amber-400 font-mono">
                {proCount}/{TARGET_PRO_COUNT} Pro
              </span>
            </div>
            <Progress value={progressPercent} className="h-3 mb-2" />
            <div className="flex justify-between text-xs text-slate-400">
              <span>
                {remainingForFreeMonth > 0 
                  ? `${remainingForFreeMonth} Pro daha = 1 ay ücretsiz!` 
                  : '🎉 Hedef tamamlandı!'
                }
              </span>
              <span>~₺{(proCount * AVG_COMMISSION).toFixed(0)} / ₺{MONTHLY_PRICE}</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-2 text-center">
              💡 Kazandığın krediler otomatik olarak bir sonraki abonelik ödemenden düşer.
            </p>
          </CardContent>
        </Card>

        {/* Ana Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          
          {/* Referans Kodun */}
          <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-emerald-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Gift className="h-5 w-5 text-primary" />
                Senin Referans Kodun
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Kod */}
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg mb-4">
                <span className="text-2xl font-mono font-bold text-primary tracking-wider">
                  {referralData?.code || '---'}
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={copyCode}
                  className="border-primary/50 hover:bg-primary/20"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1 text-green-400" /> Kopyalandı
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" /> Kopyala
                    </>
                  )}
                </Button>
              </div>

              {/* Link - Kısa format */}
              <div className="p-3 bg-slate-800/30 rounded-lg mb-5">
                <p className="text-xs text-slate-500 mb-1">Kısa Paylaşım Linki</p>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-primary truncate flex-1 font-mono">
                    {referralData?.share_url || '---'}
                  </p>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={copyLink}
                    className="h-8 w-8"
                  >
                    {copiedLink ? (
                      <Check className="h-4 w-4 text-green-400" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Büyük Sosyal Paylaşım Butonları */}
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-2">Mesajı önizle ve paylaş</p>
                <Button 
                  onClick={shareOnWhatsApp}
                  className="w-full bg-green-600 hover:bg-green-700 text-white justify-start h-12"
                >
                  <MessageCircle className="h-5 w-5 mr-3" />
                  WhatsApp&apos;ta Paylaş
                  <span className="ml-auto text-xs opacity-70 flex items-center gap-1">
                    <Eye className="h-3 w-3" /> Önizle
                  </span>
                </Button>
                <Button 
                  onClick={shareOnTelegram}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white justify-start h-12"
                >
                  <Send className="h-5 w-5 mr-3" />
                  Telegram&apos;da Paylaş
                  <span className="ml-auto text-xs opacity-70 flex items-center gap-1">
                    <Eye className="h-3 w-3" /> Önizle
                  </span>
                </Button>
                <Button 
                  onClick={shareOnTwitter}
                  variant="outline"
                  className="w-full justify-start h-12"
                >
                  <Twitter className="h-5 w-5 mr-3" />
                  Twitter&apos;da Paylaş
                  <span className="ml-auto text-xs opacity-70 flex items-center gap-1">
                    <Eye className="h-3 w-3" /> Önizle
                  </span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Cüzdan */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Wallet className="h-5 w-5 text-amber-400" />
                Cüzdanın
              </CardTitle>
              <CardDescription>
                Krediler bir sonraki yenilemende otomatik kullanılır
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Bakiye */}
              <div className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg mb-4">
                <p className="text-xs text-amber-400 mb-1">Üyelik Kredisi</p>
                <p className="text-3xl font-bold text-white">
                  ₺{(walletData?.balance || 0).toFixed(0)}
                </p>
                {(walletData?.balance || 0) > 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    Sonraki yenilemende otomatik düşülür
                  </p>
                )}
              </div>

              {/* İstatistikler */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-500">Toplam Kazanç</p>
                  <p className="text-lg font-semibold text-green-400">
                    ₺{(walletData?.lifetime_earnings || 0).toFixed(0)}
                  </p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-500">Bekleyen</p>
                  <p className="text-lg font-semibold text-slate-300">
                    ₺{(walletData?.pending_earnings || 0).toFixed(0)}
                  </p>
                </div>
              </div>

              {/* Kazanç Tablosu */}
              <div className="p-4 bg-slate-800/30 border border-slate-700/50 rounded-lg">
                <p className="font-medium text-white text-sm mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  Her Pro&apos;dan ne kazanırsın?
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Aylık Pro (₺590)</span>
                    <span className="text-primary font-semibold">+₺100 kredi</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Yıllık Pro (₺4990)</span>
                    <span className="text-primary font-semibold">+₺848 kredi</span>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-3">
                  * İndirimli ödeme tutarının %20&apos;si
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Nasıl Çalışır - Kompakt */}
        <Card className="mb-8 bg-slate-900/50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-primary" />
              3 Adımda Kazan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                  1
                </div>
                <div>
                  <p className="font-medium text-sm">Linki Paylaş</p>
                  <p className="text-xs text-slate-500">WhatsApp, Telegram...</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold text-sm">
                  2
                </div>
                <div>
                  <p className="font-medium text-sm">Arkadaşın Pro Olsun</p>
                  <p className="text-xs text-slate-500">%15 indirimle başlar</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold text-sm">
                  3
                </div>
                <div>
                  <p className="font-medium text-sm">Kredi Kazan</p>
                  <p className="text-xs text-slate-500">%20 anında cüzdanına</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Davet Listesi - Feed */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5 text-blue-400" />
                Davet Ettiğin Kişiler
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {statsData?.total_referrals || 0} davet
                </Badge>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                  {statsData?.converted_referrals || 0} Pro
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Özet Kartları */}
            <div className="grid grid-cols-4 gap-2 mb-6">
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                <p className="text-xl font-bold text-blue-400">{statsData?.total_referrals || 0}</p>
                <p className="text-[10px] text-slate-500">Toplam</p>
              </div>
              <div className="p-3 bg-slate-500/10 border border-slate-500/30 rounded-lg text-center">
                <p className="text-xl font-bold text-slate-400">
                  {Math.max((statsData?.total_referrals || 0) - (statsData?.converted_referrals || 0) - (statsData?.pending_referrals || 0), 0)}
                </p>
                <p className="text-[10px] text-slate-500">Kayıt</p>
              </div>
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
                <p className="text-xl font-bold text-amber-400">{statsData?.pending_referrals || 0}</p>
                <p className="text-[10px] text-slate-500">Bekliyor</p>
              </div>
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                <p className="text-xl font-bold text-green-400">{statsData?.converted_referrals || 0}</p>
                <p className="text-[10px] text-slate-500">Pro</p>
              </div>
            </div>

            {/* Referans Listesi - Feed */}
            {statsData?.referrals && statsData.referrals.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-2">Son davetlerin</p>
                {statsData.referrals.slice(0, 5).map((ref, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                        ref.status === 'converted' 
                          ? 'bg-green-500/20 text-green-400' 
                          : ref.status === 'registered' 
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-slate-700 text-slate-400'
                      }`}>
                        {ref.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{ref.name || 'Bilinmeyen'}</p>
                        <p className="text-xs text-slate-500">{ref.email}</p>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-1">
                      {getStatusBadge(ref.status)}
                      {ref.status === 'converted' && ref.commission > 0 && (
                        <span className="text-xs text-green-400 flex items-center gap-1">
                          <Coins className="h-3 w-3" />
                          +₺{ref.commission?.toFixed(0)} kredi yüklendi
                        </span>
                      )}
                      {ref.status === 'registered' && (
                        <span className="text-[10px] text-slate-500">
                          Pro olunca kredi yüklenir
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium mb-1">Henüz kimseyi davet etmedin</p>
                <p className="text-sm text-slate-500 mb-4">İlk davetini yap, kredi kazanmaya başla!</p>
                <Button 
                  onClick={shareOnWhatsApp}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp&apos;ta Paylaş
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Son İşlemler */}
        {walletData?.transactions && walletData.transactions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="h-5 w-5 text-green-400" />
                Kredi Geçmişi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {walletData.transactions.map((tx, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-500/20 rounded-full">
                        <Coins className="h-4 w-4 text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{tx.description}</p>
                        <p className="text-xs text-slate-500">{tx.reference}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-semibold">+₺{tx.amount?.toFixed(0)}</p>
                      <p className="text-xs text-slate-500">
                        {tx.date ? new Date(tx.date).toLocaleDateString('tr-TR') : '-'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-slate-400 mb-4">
            Henüz Pro değil misin? Bir arkadaşının kodunu kullanarak %15 indirimle başla!
          </p>
          <Button onClick={() => navigate('/pricing')} variant="outline">
            <Crown className="h-4 w-4 mr-2" />
            Pro&apos;ya Geç
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>

        {/* Mini SSS - İtiraz Kırıcı */}
        <div className="mt-8 border-t border-slate-800 pt-6">
          <p className="text-xs text-slate-500 text-center mb-4">Sık Sorulan Sorular</p>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-slate-800/30 rounded-lg">
              <p className="font-medium text-slate-300 mb-1">Kredi nakit çekilir mi?</p>
              <p className="text-xs text-slate-500">Hayır, sadece üyelik yenilemesinde kullanılır.</p>
            </div>
            <div className="p-3 bg-slate-800/30 rounded-lg">
              <p className="font-medium text-slate-300 mb-1">Ne zaman yüklenir?</p>
              <p className="text-xs text-slate-500">Arkadaşın ödeme yaptığı anda otomatik yüklenir.</p>
            </div>
            <div className="p-3 bg-slate-800/30 rounded-lg">
              <p className="font-medium text-slate-300 mb-1">İndirimli ödeme geçerli mi?</p>
              <p className="text-xs text-slate-500">Evet, indirimli tutarın %20&apos;si kredi olarak yazılır.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralWalletPage;
